import { FormControlLabel, Switch } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { List } from 'components/reusable/list';
import { useGlobalState } from 'store/reducers/reducer';
import { loadAccountGroupConfigs, loadAdminAccountGroups, updateGroupConfig } from 'store/sagas/sagas';
function buildAccountGroupConfigRows(groups, configs) {
    // Normalize keys to strings to guard against BIGINT being returned as string by node-postgres vs number from other columns
    const accountLevelConfig = configs.find((c) => c.accountGroupId === null);
    const configByGroupId = new Map(configs
        .filter((c) => c.accountGroupId !== null)
        .map((c) => [String(c.accountGroupId), c]));
    const accountLevelRow = {
        key: 'account-level',
        groupId: null,
        groupName: 'None [null]',
        hasConfig: accountLevelConfig !== undefined,
        onlineDeduplicationEnabled: accountLevelConfig?.onlineDeduplicationEnabled ?? false,
        stablePatternEnabled: accountLevelConfig?.faceVelocityEnabled ?? false,
        configId: accountLevelConfig?.id ?? null
    };
    const groupRows = groups.map((group) => {
        const config = configByGroupId.get(String(group.id));
        return {
            key: String(group.id),
            groupId: group.id,
            groupName: group.name,
            hasConfig: config !== undefined,
            onlineDeduplicationEnabled: config?.onlineDeduplicationEnabled ?? false,
            stablePatternEnabled: config?.faceVelocityEnabled ?? false,
            configId: config?.id ?? null
        };
    });
    return [accountLevelRow, ...groupRows];
}
export const AccountGroupConfig = () => {
    const { asyncDispatch } = useGlobalState();
    const { id } = useParams();
    const [rows, setRows] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [savingKeys, setSavingKeys] = useState(new Set());
    useEffect(() => {
        const accountId = Number(id);
        if (!accountId)
            return;
        setIsLoading(true);
        Promise.all([
            asyncDispatch(loadAdminAccountGroups(accountId)),
            asyncDispatch(loadAccountGroupConfigs(accountId))
        ])
            .then(([groups, configs]) => {
            setRows(buildAccountGroupConfigRows(groups, configs));
        })
            .catch(console.error)
            .finally(() => {
            setIsLoading(false);
        });
    }, [id]);
    const handleToggle = useCallback((row, checked) => {
        setSavingKeys((prev) => {
            return new Set([...prev, row.key]);
        });
        asyncDispatch(updateGroupConfig(Number(id), {
            accountGroupId: row.groupId,
            onlineDeduplicationEnabled: row.onlineDeduplicationEnabled,
            faceVelocityEnabled: checked
        }))
            .then((updated) => {
            setRows((prev) => prev?.map((r) => r.key === row.key
                ? {
                    ...r,
                    stablePatternEnabled: updated.faceVelocityEnabled,
                    configId: updated.id
                }
                : r) ?? null);
        })
            .catch(console.error)
            .finally(() => {
            setSavingKeys((prev) => {
                const next = new Set(prev);
                next.delete(row.key);
                return next;
            });
        });
    }, [id, asyncDispatch]);
    return (React.createElement("div", { className: "full-page" },
        React.createElement("div", { className: "groups-main" },
            React.createElement("h1", { className: "g-page-name" }, "Stable Pattern Configuration"),
            React.createElement("p", { style: { color: '#666', fontSize: 14 } }, "(formerly Face Velocity)"),
            React.createElement(List, { columns: [
                    {
                        name: 'Group',
                        xs: 4,
                        customRenderValue: (row) => row.groupName
                    },
                    {
                        name: 'Online Deduplication',
                        xs: 4,
                        customRenderValue: (row) => row.hasConfig
                            ? row.onlineDeduplicationEnabled
                                ? 'Enabled'
                                : 'Disabled'
                            : 'None'
                    },
                    {
                        name: 'Stable Pattern',
                        isClickable: false,
                        xs: 4,
                        customRenderValue: (row) => (React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: row.stablePatternEnabled, disabled: !row.onlineDeduplicationEnabled ||
                                    savingKeys.has(row.key), onChange: (_e, checked) => {
                                    handleToggle(row, checked);
                                }, size: "small" }), label: row.onlineDeduplicationEnabled
                                ? savingKeys.has(row.key)
                                    ? 'Saving...'
                                    : ''
                                : 'Requires deduplication', style: { marginLeft: 0 } }))
                    }
                ], items: rows, label: "configs", loading: isLoading, total: rows?.length ?? 0 }))));
};
